import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  SunIcon,
  MoonIcon,
  UserCircleIcon,
  CogIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  UsersIcon,
  KeyIcon,
  ExclamationTriangleIcon,
  ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';
import { useAuthBusiness } from '../../contexts/AuthBusinessContext';
import { db } from '../../services/firebase';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';

// Logo imports from public folder (same as PublicHeader)
const logoLight = process.env.PUBLIC_URL + '/icons/sp-logo-blue.svg';
const logoDark = process.env.PUBLIC_URL + '/icons/sp-logo-white.svg';
const iconLight = process.env.PUBLIC_URL + '/icons/sp-fav-blue.svg';
const iconDark = process.env.PUBLIC_URL + '/icons/sp-fav-blue.svg';

const AdminHeader = ({ onMenuToggle, darkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const { currentUser, selectedBusiness } = useAuthBusiness(); // Auth context for user info

  // Get real user data from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const adminData = {
    name: user.name || 'Admin Kullanıcı',
    email: user.email || 'admin@prmanager.com',
    role: user.user_type === 'admin' ? 'Super Admin' : 'Admin',
    avatar: null,
    notifications: [
      {
        id: 1,
        type: 'api_request',
        title: 'Yeni API Talebi',
        message: 'Ahmet Kaya Instagram API erişimi talep etti',
        time: '2 dk önce',
        priority: 'high'
      },
      {
        id: 2,
        type: 'security',
        title: 'Güvenlik Uyarısı',
        message: 'Şüpheli API aktivitesi tespit edildi',
        time: '15 dk önce',
        priority: 'critical'
      },
      {
        id: 3,
        type: 'user',
        title: 'Yeni Kullanıcı',
        message: 'Fatma Demir sisteme kaydoldu',
        time: '1 saat önce',
        priority: 'normal'
      }
    ],
    pendingTasks: {
      apiRequests: 5,
      securityReviews: 2,
      userApprovals: 3
    }
  };

  const [notifications, setNotifications] = React.useState([]);

  React.useEffect(() => {
    if (!db) return;

    const q = query(
      collection(db, 'notifications'),
      // Admin sees everything or target='admin' if you implement targeting
      // For now, let's show all 'sent' or 'warning' types
      where('status', '==', 'sent'),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Map Firestore fields to UI expected fields
        time: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate().toLocaleDateString('tr-TR') : 'Bugün',
        priority: doc.data().type === 'warning' ? 'high' : doc.data().type === 'error' ? 'critical' : 'normal'
      }));
      setNotifications(notifs);
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('selectedBusiness');

    // Auth state change event'ini tetikle
    window.dispatchEvent(new Event('authStateChanged'));

    navigate('/login');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'api_request':
        return <KeyIcon className="w-5 h-5 text-blue-500" />;
      case 'security':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'user':
        return <UsersIcon className="w-5 h-5 text-green-500" />;
      default:
        return <BellIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 border-red-500';
      case 'high':
        return 'bg-orange-100 border-orange-500';
      case 'normal':
        return 'bg-blue-100 border-blue-500';
      default:
        return 'bg-gray-100 border-gray-500';
    }
  };

  return (
    <>
      <nav 
        className={`relative flex flex-wrap items-center justify-between px-4 py-2.5 w-full transition-all shadow-sm z-[45] lg:flex-nowrap lg:justify-start ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-slate-100 text-slate-700'} border-b`}
        style={{
          paddingTop: 'max(env(safe-area-inset-top, 0px), 0.625rem)'
        }}
      >

        <div className="flex items-center justify-between w-full px-4 py-1 mx-auto flex-wrap-inherit">
          {/* Logo Section */}
          <div className="flex items-center">
            <Link to="/dashboard/admin/" className="flex items-center">
              <img src={darkMode ? iconDark : iconLight} alt="icon" className="h-6 mr-2" />
              <img
                src={darkMode ? logoDark : logoLight}
                className="inline h-8 max-w-full transition-all duration-200 ease-nav-brand"
                alt="Sectorpilot"
              />
            </Link>
          </div>

          <div className="flex items-center mt-2 grow sm:mt-0 sm:mr-6 md:mr-0 lg:flex lg:basis-auto">
            <div className="flex items-center md:ml-auto md:pr-4">
              {/* Mobile menu button */}
              <button
                onClick={onMenuToggle}
                className="inline-block p-2 mr-2 text-center text-slate-500 cursor-pointer rounded-lg lg:hidden"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>

              {/* Admin Search - daha spesifik */}
              <div className="relative flex flex-wrap items-stretch w-full transition-all rounded-lg">
                <span className="text-sm ease-soft leading-5.6 absolute z-50 -ml-px flex h-full items-center whitespace-nowrap rounded-lg rounded-tr-none rounded-br-none border border-r-0 border-transparent bg-transparent py-2 px-2.5 text-center font-normal text-slate-500 transition-all">
                  <svg width="14px" height="14px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.7955 15.8111L21 21M18 10.5C18 14.6421 14.6421 18 10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <input
                  type="text"
                  className={`pl-9 text-sm focus:shadow-soft-primary-outline ease-soft w-1/100 leading-5.6 relative -ml-px block min-w-0 flex-auto rounded-lg border border-solid border-gray-300 ${darkMode ? 'bg-gray-700 text-white placeholder:text-gray-400 border-gray-600' : 'bg-white text-gray-700'} bg-clip-padding py-2 pr-3 transition-all focus:border-fuchsia-300 focus:outline-none focus:transition-shadow`}
                  placeholder="Kullanıcı, API, rapor ara..."
                />
              </div>
            </div>

            {/* Support Center Button for Admin */}
            <div className="hidden lg:flex items-center mr-4">
              <button
                onClick={() => navigate('/dashboard/admin/support')}
                className={`p-2 rounded-lg transition-colors flex items-center gap-2 ${darkMode ? 'text-white hover:bg-gray-700' : 'text-slate-600 hover:bg-gray-100'}`}
                title="Destek Merkezi"
              >
                <ChatBubbleBottomCenterTextIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Destek</span>
              </button>
            </div>

            {/* Admin Panel Quick Actions */}
            <div className="hidden lg:flex items-center space-x-2 mr-4">
              <Link
                to="/dashboard/admin/users"
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-white hover:bg-gray-700' : 'text-slate-600 hover:bg-gray-100'}`}
                title="Kullanıcı Yönetimi"
              >
                <UsersIcon className="w-5 h-5" />
              </Link>
              <Link
                to="/dashboard/admin/api-requests"
                className={`p-2 rounded-lg transition-colors relative ${darkMode ? 'text-white hover:bg-gray-700' : 'text-slate-600 hover:bg-gray-100'}`}
                title="API Talepleri"
              >
                <KeyIcon className="w-5 h-5" />
                {adminData.pendingTasks.apiRequests > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {adminData.pendingTasks.apiRequests}
                  </span>
                )}
              </Link>
              <Link
                to="/dashboard/admin/security"
                className={`p-2 rounded-lg transition-colors relative ${darkMode ? 'text-white hover:bg-gray-700' : 'text-slate-600 hover:bg-gray-100'}`}
                title="Güvenlik İncelemesi"
              >
                <ShieldCheckIcon className="w-5 h-5" />
                {adminData.pendingTasks.securityReviews > 0 && (
                  <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {adminData.pendingTasks.securityReviews}
                  </span>
                )}
              </Link>
              <Link
                to="/dashboard/admin/analytics"
                className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-white hover:bg-gray-700' : 'text-slate-600 hover:bg-gray-100'}`}
                title="Analitik"
              >
                <ChartBarIcon className="w-5 h-5" />
              </Link>
            </div>

            {/* Right side icons */}
            <ul className="flex flex-row justify-end pl-0 mb-0 list-none md-max:w-full">
              {/* Dark mode toggle */}
              <li className="flex items-center">
                <button
                  onClick={toggleDarkMode}
                  className={`p-2 text-sm font-semibold transition-all ease-nav-brand rounded-full ${darkMode ? 'text-white hover:bg-gray-700' : 'text-slate-500 hover:bg-gray-100'}`}
                >
                  {darkMode ? (
                    <SunIcon className="w-5 h-5" />
                  ) : (
                    <MoonIcon className="w-5 h-5" />
                  )}
                </button>
              </li>

              {/* Admin Notifications */}
              <li className="flex items-center px-4">
                <Menu as="div" className="relative">
                  <Menu.Button className={`p-2 text-sm font-semibold transition-all ease-nav-brand rounded-full relative ${darkMode ? 'text-white hover:bg-gray-700' : 'text-slate-500 hover:bg-gray-100'}`}>
                    <BellIcon className="w-5 h-5" />
                    {notifications.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {notifications.length}
                      </span>
                    )}
                  </Menu.Button>
                  <Menu.Items className={`absolute right-0 mt-2 w-80 rounded-lg shadow-lg z-50 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Admin Bildirimleri
                      </h3>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {notifications.length} yeni bildirim
                      </p>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {notifications.map((notification) => (
                        <Menu.Item key={notification.id}>
                          {({ active }) => (
                            <div className={`p-4 border-l-4 ${getPriorityColor(notification.priority)} ${active ? (darkMode ? 'bg-gray-700' : 'bg-gray-50') : ''}`}>
                              <div className="flex items-start space-x-3">
                                {getNotificationIcon(notification.type)}
                                <div className="flex-1">
                                  <h4 className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {notification.title}
                                  </h4>
                                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {notification.message}
                                  </p>
                                  <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                                    {notification.time}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                      <Link
                        to="/dashboard/admin/notifications"
                        className={`text-sm font-medium ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
                      >
                        Tüm bildirimleri görüntüle
                      </Link>
                    </div>
                  </Menu.Items>
                </Menu>
              </li>

              {/* Admin Profile */}
              <li className="flex items-center px-4">
                <Menu as="div" className="relative">
                  <Menu.Button className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {adminData.name.split(' ').map(n => n.charAt(0)).join('')}
                      </span>
                    </div>
                    <div className="hidden md:block text-left">
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {adminData.name}
                      </p>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {adminData.role}
                      </p>
                    </div>
                  </Menu.Button>
                  <Menu.Items className={`absolute right-0 mt-2 w-56 rounded-lg shadow-lg z-50 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {adminData.name}
                      </p>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {adminData.email}
                      </p>
                      <div className="mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <ShieldCheckIcon className="w-3 h-3 mr-1" />
                          {adminData.role}
                        </span>
                      </div>
                    </div>
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/dashboard/admin/AdminProfile"
                            className={`flex items-center px-4 py-2 text-sm ${active ? (darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900') : (darkMode ? 'text-gray-300' : 'text-gray-700')}`}
                          >
                            <UserCircleIcon className="w-4 h-4 mr-3" />
                            Profil Ayarları
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/dashboard/admin/settings"
                            className={`flex items-center px-4 py-2 text-sm ${active ? (darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900') : (darkMode ? 'text-gray-300' : 'text-gray-700')}`}
                          >
                            <CogIcon className="w-4 h-4 mr-3" />
                            Sistem Ayarları
                          </Link>
                        )}
                      </Menu.Item>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleLogout}
                            className={`flex items-center w-full px-4 py-2 text-sm text-left ${active ? (darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900') : (darkMode ? 'text-gray-300' : 'text-gray-700')}`}
                          >
                            <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Çıkış Yap
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Menu>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </>
  );
};

export default AdminHeader;
